<template>
    <div class="chatbot-status-widget">
        <md-button
            ref="buttonRef"
            style="min-width: 20px; max-width: 25px"
            class="actionButtonBreadcrumb m-0 chatbot-btn chatbot-status-button"
            @mouseenter="chatbotIconHovered = true"
            @mouseleave="chatbotIconHovered = false"
            @click="toggleDropdown"
        >
            <ChatbotIcon
                class="chatbot-icon"
                :color="chatbotIconHovered || isDropdownVisible ? getIconColor() : '#3A3F48'"
            />
        </md-button>

        <!-- Custom Dropdown -->
        <div v-if="isDropdownVisible" class="chatbot-custom-dropdown" :style="dropdownStyle" :class="{ 'chatbot-dropdown-ready': dropdownReady }">
            <div class="chatbot-dropdown-content">
                <!-- Chatbot Header -->
                <div class="chatbot-section-header">
                    <div class="chatbot-header-left">
                        <span class="chatbot-header-title">{{ $t("message.chatbotStatsHeaderTitle") }}</span>
                        <div class="chatbot-info-wrapper">
                            <md-icon
                                class="chatbot-info-icon"
                                @mouseenter.native="showInfoTooltip"
                                @mouseleave.native="hideInfoTooltip"
                            >info_outline</md-icon>
                        </div>
                    </div>
                    <div class="chatbot-status-indicator" :class="getStatusClass()">
                        <span class="chatbot-status-dot"></span>
                        <span class="chatbot-status-text">{{ getStatusText() }}</span>
                    </div>
                </div>

                <!-- Total Sessions -->
                <div class="chatbot-stat-row">
                    <div class="chatbot-stat-left">
                        <div class="chatbot-icon-box total">
                            <md-icon class="chatbot-stat-icon">forum</md-icon>
                        </div>
                        <div class="chatbot-stat-details">
                            <div class="chatbot-stat-title">{{ $t("message.chatbotStatTotalChatbotsTitle") }}</div>
                            <div class="chatbot-stat-desc">{{ $t('message.maxSessionCount') }}</div>
                        </div>
                    </div>
                    <div class="chatbot-stat-number total">{{ (features.totalSessionCount || 0).toLocaleString() }}</div>
                </div>

                <!-- Free Sessions -->
                <div class="chatbot-stat-row">
                    <div class="chatbot-stat-left">
                        <div class="chatbot-icon-box free">
                            <md-icon class="chatbot-stat-icon">card_giftcard</md-icon>
                        </div>
                        <div class="chatbot-stat-details">
                            <div class="chatbot-stat-title">{{ $t("message.chatbotStatFreeTitle") }}</div>
                            <div class="chatbot-stat-desc">{{ $t("message.chatbotStatFreeDesc") }}</div>
                        </div>
                    </div>
                    <div class="chatbot-stat-number free">{{ (features.freeSessionCount || 0).toLocaleString() }}</div>
                </div>

                <!-- Extra Sessions -->
                <div class="chatbot-stat-row">
                    <div class="chatbot-stat-left">
                        <div class="chatbot-icon-box extra">
                            <md-icon class="chatbot-stat-icon">bolt</md-icon>
                        </div>
                        <div class="chatbot-stat-details">
                            <div class="chatbot-stat-title">{{ $t("message.chatbotStatExtraTitle") }}</div>
                            <div class="chatbot-stat-desc">{{ $t("message.chatbotStatExtraDesc") }}</div>
                        </div>
                    </div>
                    <div class="chatbot-stat-number extra">{{ (features.extraSessionCount || 0).toLocaleString() }}</div>
                </div>

                <!-- Used Sessions (Expandable) -->
                <div class="chatbot-stat-row chatbot-expandable" @click="usageExpanded = !usageExpanded">
                    <div class="chatbot-stat-left">
                        <div class="chatbot-icon-box used">
                            <md-icon class="chatbot-stat-icon">check_circle</md-icon>
                        </div>
                        <div class="chatbot-stat-details">
                            <div class="chatbot-stat-title">
                                {{ $t("message.chatbotStatUsedTitle") }}
                                <md-icon class="chatbot-expand-icon" :class="{ expanded: usageExpanded }">expand_more</md-icon>
                            </div>
                            <div class="chatbot-stat-desc">
                                {{ getUsagePercentage().toFixed(1) }}{{ $t("message.chatbotPercentUsedSuffix") }}
                            </div>
                        </div>
                    </div>
                    <div class="chatbot-stat-number used">{{ (totalChatbotUsage || 0).toLocaleString() }}</div>
                </div>

                <!-- Usage Breakdown (Sub-rows) -->
                <div v-if="usageExpanded" class="chatbot-usage-breakdown">
                    <div class="chatbot-breakdown-row">
                        <div class="chatbot-breakdown-left">
                            <span class="chatbot-breakdown-dot free"></span>
                            <span class="chatbot-breakdown-label">{{ $t("message.chatbotBreakdownFreeCredits") }}</span>
                        </div>
                        <span class="chatbot-breakdown-value free">{{ (usedFromFree || 0).toLocaleString() }}</span>
                    </div>
                    <div class="chatbot-breakdown-row">
                        <div class="chatbot-breakdown-left">
                            <span class="chatbot-breakdown-dot normal"></span>
                            <span class="chatbot-breakdown-label">{{ $t("message.chatbotBreakdownNormalPlan") }}</span>
                        </div>
                        <span class="chatbot-breakdown-value normal">{{ (usedFromNormal || 0).toLocaleString() }}</span>
                    </div>
                    <div class="chatbot-breakdown-row">
                        <div class="chatbot-breakdown-left">
                            <span class="chatbot-breakdown-dot extra"></span>
                            <span class="chatbot-breakdown-label">{{ $t("message.chatbotBreakdownExtraPlan") }}</span>
                        </div>
                        <span class="chatbot-breakdown-value extra">{{ (usedFromExtra || 0).toLocaleString() }}</span>
                    </div>
                </div>

                <!-- Remaining Sessions -->
                <div class="chatbot-stat-row">
                    <div class="chatbot-stat-left">
                        <div class="chatbot-icon-box remaining" :class="getStatusClass()">
                            <md-icon class="chatbot-stat-icon">trending_up</md-icon>
                        </div>
                        <div class="chatbot-stat-details">
                            <div class="chatbot-stat-title">{{ $t("message.chatbotStatRemainingTitle") }}</div>
                            <div class="chatbot-stat-desc">{{ $t("message.chatbotStatRemainingDesc") }}</div>
                        </div>
                    </div>
                    <div class="chatbot-stat-number remaining" :class="getStatusClass()">
                        {{ getRemainingSessionCount().toLocaleString() }}
                    </div>
                </div>

                <!-- Progress Bar -->
                <div class="chatbot-progress-wrapper">
                    <div class="chatbot-progress-container">
                        <div class="chatbot-progress-bg">
                            <div class="chatbot-progress-fill" :style="{ width: getUsagePercentage() + '%' }" :class="getStatusClass()"></div>
                        </div>
                    </div>
                </div>

                <!-- Divider -->
                <div class="chatbot-divider"></div>

                <!-- Automation Header -->
                <div class="chatbot-section-header">
                    <span class="chatbot-header-title">{{ $t("message.chatbotAutomationSectionTitle") }}</span>
                    <div class="chatbot-status-indicator" :class="getAutomationStatusClass()">
                        <span class="chatbot-status-dot"></span>
                        <span class="chatbot-status-text">{{ getAutomationStatusText() }}</span>
                    </div>
                </div>

                <!-- Total Automation -->
                <div class="chatbot-stat-row">
                    <div class="chatbot-stat-left">
                        <div class="chatbot-icon-box automation">
                            <md-icon class="chatbot-stat-icon">smart_toy</md-icon>
                        </div>
                        <div class="chatbot-stat-details">
                            <div class="chatbot-stat-title">{{ $t("message.chatbotStatTotalAutomationTitle") }}</div>
                            <div class="chatbot-stat-desc">{{ $t('message.maxAutomationTask') }}</div>
                        </div>
                    </div>
                    <div class="chatbot-stat-number automation">{{ (features.maxAutomationTask || 0).toLocaleString() }}</div>
                </div>

                <!-- Used Automation -->
                <div class="chatbot-stat-row">
                    <div class="chatbot-stat-left">
                        <div class="chatbot-icon-box used-automation">
                            <md-icon class="chatbot-stat-icon">task_alt</md-icon>
                        </div>
                        <div class="chatbot-stat-details">
                            <div class="chatbot-stat-title">{{ $t("message.chatbotStatUsedAutomationTitle") }}</div>
                            <div class="chatbot-stat-desc">
                                {{ getAutomationUsagePercentage().toFixed(1) }}{{ $t("message.chatbotPercentUsedSuffix") }}
                            </div>
                        </div>
                    </div>
                    <div class="chatbot-stat-number used-automation">{{ (totalAutomationUsage || 0).toLocaleString() }}</div>
                </div>

                <!-- Remaining Automation Task -->
                <div class="chatbot-stat-row">
                    <div class="chatbot-stat-left">
                        <div class="chatbot-icon-box remaining-auto" :class="getAutomationStatusClass()">
                            <md-icon class="chatbot-stat-icon">hourglass_empty</md-icon>
                        </div>
                        <div class="chatbot-stat-details">
                            <div class="chatbot-stat-title">{{ $t("message.chatbotStatRemainingAutomationTitle") }}</div>
                            <div class="chatbot-stat-desc">{{ $t('message.remainingAutomationTask') }}</div>
                        </div>
                    </div>
                    <div class="chatbot-stat-number remaining-auto" :class="getAutomationStatusClass()">
                        {{ getRemainingAutomationTask().toLocaleString() }}
                    </div>
                </div>

                <!-- Automation Progress Bar -->
                <div class="chatbot-progress-wrapper">
                    <div class="chatbot-progress-container">
                        <div class="chatbot-progress-bg">
                            <div class="chatbot-progress-fill" :style="{ width: getAutomationUsagePercentage() + '%' }" :class="getAutomationStatusClass()"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Info Tooltip (fixed position, dropdown dışında) -->
        <div v-if="infoTooltipVisible" class="chatbot-info-tooltip-fixed" :style="infoTooltipStyle">
            <div class="tooltip-title">{{ $t("message.chatbotUsagePriorityTooltipTitle") }}</div>
            <div class="tooltip-item">
                <span class="tooltip-badge free">1</span>
                <span>{{ $t("message.chatbotUsagePriorityStep1") }}</span>
            </div>
            <div class="tooltip-item">
                <span class="tooltip-badge normal">2</span>
                <span>{{ $t("message.chatbotUsagePriorityStep2") }}</span>
            </div>
            <div class="tooltip-item">
                <span class="tooltip-badge extra">3</span>
                <span>{{ $t("message.chatbotUsagePriorityStep3") }}</span>
            </div>
            <div class="tooltip-note">{{ $t("message.chatbotUsagePriorityNote") }}</div>
        </div>

        <!-- Backdrop to close dropdown -->
        <div v-if="isDropdownVisible" class="dropdown-backdrop" @click="closeDropdown"></div>
    </div>
</template>

<script>
import { mapState } from "vuex";
import ChatbotIcon from "../svg/appBreadCrumb/ChatbotIcon.vue";

/**
 * Chatbot istatistiklerini dropdown olarak gösteren widget komponenti
 * ChannelStatusWidget.vue benzeri yapıda, click ile açılır/kapanır
 */
export default {
    name: "ChatbotStatusWidget",
    components: { ChatbotIcon },
    data() {
        return {
            isDropdownVisible: false,
            chatbotIconHovered: false,
            dropdownStyle: {},
            dropdownReady: false,
            usageExpanded: false,
            infoTooltipVisible: false,
            infoTooltipStyle: {},
        };
    },
    computed: {
        ...mapState({
            features: (state) => state.site.features || {},
            totalChatbotUsage: (state) => state.site.conversationStatistics.totalChatbotUsage || 0,
            totalAutomationUsage: (state) => state.site.conversationStatistics.totalAutomationUsage || 0,
            usedFromFree: (state) => state.site.conversationStatistics.usedFromFree || 0,
            usedFromNormal: (state) => state.site.conversationStatistics.usedFromNormal || 0,
            usedFromExtra: (state) => state.site.conversationStatistics.usedFromExtra || 0,
        }),
    },
    methods: {
        /**
         * Dropdown pozisyonunu hesaplar ve günceller
         */
        updateDropdownPosition() {
            if (!this.$refs.buttonRef) return;
            
            const buttonRect = this.$refs.buttonRef.$el.getBoundingClientRect();
            const dropdownWidth = 300;
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const padding = 16;
            
            let top = buttonRect.bottom + 8;
            let left = buttonRect.left + (buttonRect.width / 2);
            
            // Sağ tarafa taşıyor mu kontrol et
            const wouldOverflowRight = left + (dropdownWidth / 2) > viewportWidth - padding;
            // Sol tarafa taşıyor mu kontrol et
            const wouldOverflowLeft = left - (dropdownWidth / 2) < padding;
            
            if (wouldOverflowRight) {
                this.dropdownStyle = {
                    top: `${top}px`,
                    right: `${padding}px`,
                    left: 'auto',
                    transform: 'none',
                };
            } else if (wouldOverflowLeft) {
                this.dropdownStyle = {
                    top: `${top}px`,
                    left: `${padding}px`,
                    right: 'auto',
                    transform: 'none',
                };
            } else {
                this.dropdownStyle = {
                    top: `${top}px`,
                    left: `${left}px`,
                    transform: 'translateX(-50%)',
                };
            }
            
            // Alta taşıyor mu kontrol et
            this.$nextTick(() => {
                const dropdown = this.$el.querySelector('.custom-dropdown');
                if (dropdown) {
                    const dropdownRect = dropdown.getBoundingClientRect();
                    if (dropdownRect.bottom > viewportHeight - padding) {
                        this.dropdownStyle.top = `${buttonRect.top - dropdownRect.height - 8}px`;
                    }
                }
            });
        },

        /**
         * Dropdown'u toggle eder
         */
        toggleDropdown() {
            this.isDropdownVisible = !this.isDropdownVisible;
            if (this.isDropdownVisible) {
                this.dropdownReady = false;
                this.$nextTick(() => {
                    this.updateDropdownPosition();
                    setTimeout(() => {
                        this.dropdownReady = true;
                    }, 10);
                });
            } else {
                this.dropdownReady = false;
            }
        },

        /**
         * Dropdown'u kapatır
         */
        closeDropdown() {
            this.isDropdownVisible = false;
            this.dropdownReady = false;
        },

        /**
         * Info tooltip'i gösterir ve pozisyonunu hesaplar
         */
        showInfoTooltip(event) {
            var rect = event.target.getBoundingClientRect();
            var tooltipWidth = 250;
            var padding = 12;
            var left = rect.left;

            // Sağa taşıyorsa sola kaydır
            if (left + tooltipWidth > window.innerWidth - padding) {
                left = window.innerWidth - tooltipWidth - padding;
            }

            this.infoTooltipStyle = {
                top: (rect.bottom + 8) + 'px',
                left: left + 'px',
            };
            this.infoTooltipVisible = true;
        },

        /**
         * Info tooltip'i gizler
         */
        hideInfoTooltip() {
            this.infoTooltipVisible = false;
        },

        /**
         * Chatbot session durumunu hesaplar (high/medium/low)
         */
        getChatbotSessionStatus() {
            const leftPercentTile =
                (100 / (this.features.totalSessionCount || 0)) *
                ((this.features.totalSessionCount || 0) - this.totalChatbotUsage);
            const usedPercentTile =
                (100 / (this.features.totalSessionCount || 0)) * this.totalChatbotUsage;
            return {
                status:
                    leftPercentTile > 70 ? "high" : leftPercentTile <= 70 && leftPercentTile > 30 ? "medium" : "low",
                left: leftPercentTile.toFixed(1),
                used: usedPercentTile.toFixed(1),
            };
        },

        /**
         * İkon rengini döndürür
         */
        getIconColor() {
            const status = this.getChatbotSessionStatus().status;
            if (status === "high") return "rgb(111, 200, 98)";
            if (status === "medium") return "rgb(0, 164, 255)";
            return "rgb(168, 40, 40)";
        },

        /**
         * Kalan session sayısını döndürür
         */
        getRemainingSessionCount() {
            return Math.max((this.features.totalSessionCount || 0) - this.totalChatbotUsage, 0);
        },

        /**
         * Kalan automation task sayısını döndürür
         */
        getRemainingAutomationTask() {
            return Math.max((this.features.maxAutomationTask || 0) - this.totalAutomationUsage, 0);
        },

        /**
         * Kullanım yüzdesini döndürür
         */
        getUsagePercentage() {
            const total = this.features.totalSessionCount || 0;
            if (total === 0) return 0;
            return Math.min((this.totalChatbotUsage / total) * 100, 100);
        },

        /**
         * Durum sınıfını döndürür (high/medium/low)
         */
        getStatusClass() {
            return this.getChatbotSessionStatus().status;
        },

        /**
         * Durum metnini döndürür
         */
        getStatusText() {
            const status = this.getChatbotSessionStatus().status;
            if (status === "high") return this.$t("message.quotaStatusHealthy");
            if (status === "medium") return this.$t("message.quotaStatusNormal");
            return this.$t("message.quotaStatusCaution");
        },

        /**
         * Otomasyon kullanım yüzdesini döndürür
         */
        getAutomationUsagePercentage() {
            const total = this.features.maxAutomationTask || 0;
            if (total === 0) return 0;
            return Math.min((this.totalAutomationUsage / total) * 100, 100);
        },

        /**
         * Otomasyon durumunu hesaplar (high/medium/low)
         */
        getAutomationStatusClass() {
            const total = this.features.maxAutomationTask || 0;
            if (total === 0) return 'high';
            
            const remaining = total - this.totalAutomationUsage;
            const remainingPercentage = (remaining / total) * 100;
            
            if (remainingPercentage > 70) return 'high';
            if (remainingPercentage > 30) return 'medium';
            return 'low';
        },

        /**
         * Otomasyon durum metnini döndürür
         */
        getAutomationStatusText() {
            const status = this.getAutomationStatusClass();
            if (status === "high") return this.$t("message.quotaStatusHealthy");
            if (status === "medium") return this.$t("message.quotaStatusNormal");
            return this.$t("message.quotaStatusCaution");
        },
    },
    mounted() {},
};
</script>

<style lang="scss" scoped>
.chatbot-status-widget {
    position: relative;
    display: inline-block;
}

.chatbot-icon {
    width: 18px !important;
    height: 18px !important;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 18px !important;
        height: 18px !important;
    }
}

.chatbot-status-button {
    background: none !important;
    box-shadow: none !important;
    border: none !important;

    &:hover,
    &:focus,
    &:active {
        background: none !important;
        box-shadow: none !important;
        border: none !important;
    }

    &::before {
        display: none !important;
    }

    .md-ripple {
        display: none !important;
    }
}

// Custom Dropdown Styles
.chatbot-custom-dropdown {
    position: fixed;
    background: #F5F6FA;
    border-radius: 10px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
    z-index: 10000;
    min-width: 320px;
    max-width: 360px;
    overflow: hidden;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.15s ease-out, visibility 0.15s ease-out;
    
    &.chatbot-dropdown-ready {
        opacity: 1;
        visibility: visible;
    }

    &::before {
        content: '';
        position: absolute;
        top: -5px;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-bottom: 6px solid #F5F6FA;
        filter: drop-shadow(0 -2px 3px rgba(0, 0, 0, 0.05));
    }

    .chatbot-dropdown-content {
        display: flex;
        flex-direction: column;
        max-height: 500px;
        overflow-y: auto;
        padding: 4px;
        pointer-events: auto;

        &::-webkit-scrollbar {
            width: 4px;
        }

        &::-webkit-scrollbar-track {
            background: transparent;
            border-radius: 10px;
        }

        &::-webkit-scrollbar-thumb {
            background: #C9BBF6;
            border-radius: 10px;

            &:hover {
                background: #B8A5F0;
            }
        }

        .chatbot-section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 10px 12px;
            background: white;
            border-radius: 8px;
            margin-bottom: 6px;
            border: none !important;

            .chatbot-header-left {
                display: flex;
                align-items: center;
                gap: 6px;
            }

            .chatbot-header-title {
                font-family: Poppins, sans-serif;
                font-size: 12px;
                font-weight: 600;
                color: #1F2937;
            }

            .chatbot-info-wrapper {
                display: flex;
                align-items: center;

                .chatbot-info-icon {
                    font-size: 14px !important;
                    color: #9CA3AF;
                    cursor: pointer;
                    transition: color 0.2s ease;

                    &:hover {
                        color: #5021D1;
                    }
                }
            }

            .chatbot-status-indicator {
                display: flex;
                align-items: center;
                gap: 6px;
                padding: 4px 8px;
                border-radius: 4px;
                background: #F3F4F6;

                .chatbot-status-dot {
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    animation: pulse 2s infinite;
                }

                .chatbot-status-text {
                    font-family: Poppins, sans-serif;
                    font-size: 10px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                &.high {
                    background: #ECFDF5;
                    
                    .chatbot-status-dot {
                        background: #10B981;
                    }
                    
                    .chatbot-status-text {
                        color: #059669;
                    }
                }

                &.medium {
                    background: #FFFBEB;
                    
                    .chatbot-status-dot {
                        background: #F59E0B;
                    }
                    
                    .chatbot-status-text {
                        color: #D97706;
                    }
                }

                &.low {
                    background: #FEF2F2;
                    
                    .chatbot-status-dot {
                        background: #EF4444;
                    }
                    
                    .chatbot-status-text {
                        color: #DC2626;
                    }
                }
            }
        }

        .chatbot-usage-breakdown {
            background: #FFFFFF;
            border-radius: 8px;
            margin-bottom: 4px;
            padding: 4px 12px 8px 12px;
            animation: slideDown 0.15s ease-out;

            .chatbot-breakdown-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 5px 0;

                .chatbot-breakdown-left {
                    display: flex;
                    align-items: center;
                    gap: 8px;

                    .chatbot-breakdown-dot {
                        width: 8px;
                        height: 8px;
                        border-radius: 50%;
                        flex-shrink: 0;

                        &.free { background: #10B981; }
                        &.normal { background: #5021D1; }
                        &.extra { background: #F59E0B; }
                    }

                    .chatbot-breakdown-label {
                        font-family: Poppins, sans-serif;
                        font-size: 10px;
                        color: #6B7280;
                    }
                }

                .chatbot-breakdown-value {
                    font-family: Poppins, sans-serif;
                    font-size: 12px;
                    font-weight: 600;

                    &.free { color: #10B981; }
                    &.normal { color: #5021D1; }
                    &.extra { color: #F59E0B; }
                }
            }
        }

        .chatbot-stat-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 9px 12px;
            background: #FFFFFF;
            border-radius: 8px;
            margin-bottom: 4px;
            transition: all 0.2s ease;
            border: none !important;

            &.chatbot-expandable {
                cursor: pointer;
                user-select: none;
            }

            .chatbot-expand-icon {
                font-size: 16px !important;
                color: #9CA3AF;
                transition: transform 0.2s ease;
                vertical-align: middle;
                margin-left: 2px;

                &.expanded {
                    transform: rotate(180deg);
                }
            }

            &:last-child {
                margin-bottom: 0;
            }

            &:hover {
                background: #F8F9FA;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
            }

            .chatbot-stat-left {
                display: flex;
                align-items: center;
                gap: 10px;
                flex: 1;
                min-width: 0;

                .chatbot-icon-box {
                    width: 30px;
                    height: 30px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 7px;
                    flex-shrink: 0;

                    .chatbot-stat-icon {
                        font-size: 16px !important;
                        color: white;
                    }

                    &.total {
                        background: linear-gradient(135deg, #5021D1 0%, #4018A8 100%);
                    }

                    &.free {
                        background: linear-gradient(135deg, #10B981 0%, #059669 100%);
                    }

                    &.extra {
                        background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%);
                    }

                    &.used {
                        background: linear-gradient(135deg, #EC4899 0%, #DB2777 100%);
                    }

                    &.remaining {
                        &.high {
                            background: linear-gradient(135deg, #10B981 0%, #059669 100%);
                        }

                        &.medium {
                            background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%);
                        }

                        &.low {
                            background: linear-gradient(135deg, #EF4444 0%, #DC2626 100%);
                        }
                    }

                    &.automation {
                        background: linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%);
                    }

                    &.used-automation {
                        background: linear-gradient(135deg, #EC4899 0%, #DB2777 100%);
                    }

                    &.remaining-auto {
                        &.high {
                            background: linear-gradient(135deg, #10B981 0%, #059669 100%);
                        }

                        &.medium {
                            background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%);
                        }

                        &.low {
                            background: linear-gradient(135deg, #EF4444 0%, #DC2626 100%);
                        }
                    }
                }

                .chatbot-stat-details {
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                    min-width: 0;
                    text-align: left;

                    .chatbot-stat-title {
                        font-family: Poppins, sans-serif;
                        font-size: 11px;
                        font-weight: 600;
                        color: #1F2937;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        line-height: 1.3;
                    }

                    .chatbot-stat-desc {
                        font-family: Poppins, sans-serif;
                        font-size: 9px;
                        font-weight: 400;
                        color: #9CA3AF;
                        line-height: 1.3;
                    }
                }
            }

            .chatbot-stat-number {
                font-family: Poppins, sans-serif;
                font-size: 15px;
                font-weight: 700;
                color: #1F2937;
                flex-shrink: 0;

                &.total {
                    color: #5021D1;
                }

                &.free {
                    color: #10B981;
                }

                &.extra {
                    color: #F59E0B;
                }

                &.used {
                    color: #EC4899;
                }

                &.automation {
                    color: #8B5CF6;
                }

                &.used-automation {
                    color: #EC4899;
                }

                &.remaining-auto {
                    &.high {
                        color: #10B981;
                    }

                    &.medium {
                        color: #F59E0B;
                    }

                    &.low {
                        color: #EF4444;
                    }
                }

                &.remaining {
                    &.high {
                        color: #10B981;
                    }

                    &.medium {
                        color: #F59E0B;
                    }

                    &.low {
                        color: #EF4444;
                    }
                }
            }
        }

        .chatbot-progress-wrapper {
            padding: 10px 12px;
            background: white;
            border-radius: 8px;
            margin-bottom: 4px;
            border: none !important;

            .chatbot-progress-container {
                .chatbot-progress-bg {
                    width: 100%;
                    height: 8px;
                    background: #E5E7EB;
                    border-radius: 4px;
                    overflow: hidden;
                    position: relative;

                    .chatbot-progress-fill {
                        height: 100%;
                        border-radius: 4px;
                        transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
                        position: relative;

                        &::after {
                            content: '';
                            position: absolute;
                            top: 0;
                            left: 0;
                            right: 0;
                            bottom: 0;
                            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
                            animation: shimmer 2s infinite;
                        }

                        &.high {
                            background: linear-gradient(90deg, #10B981 0%, #34D399 100%);
                        }

                        &.medium {
                            background: linear-gradient(90deg, #F59E0B 0%, #FBBF24 100%);
                        }

                        &.low {
                            background: linear-gradient(90deg, #EF4444 0%, #F87171 100%);
                        }
                    }
                }
            }
        }

        .chatbot-divider {
            height: 1px;
            background: linear-gradient(90deg, transparent, #E5E7EB, transparent);
            margin: 8px 0;
        }
    }
}

.chatbot-info-tooltip-fixed {
    position: fixed;
    background: #1F2937;
    color: white;
    border-radius: 8px;
    padding: 10px 12px;
    width: 250px;
    z-index: 10002;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);

    .tooltip-title {
        font-family: Poppins, sans-serif;
        font-size: 10px;
        font-weight: 600;
        color: #9CA3AF;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 8px;
    }

    .tooltip-item {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 6px;
        font-family: Poppins, sans-serif;
        font-size: 11px;

        .tooltip-badge {
            width: 18px;
            height: 18px;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 10px;
            font-weight: 700;
            flex-shrink: 0;

            &.free { background: #10B981; color: white; }
            &.normal { background: #5021D1; color: white; }
            &.extra { background: #F59E0B; color: white; }
        }
    }

    .tooltip-note {
        font-family: Poppins, sans-serif;
        font-size: 10px;
        color: #9CA3AF;
        margin-top: 6px;
        font-style: italic;
    }
}

.dropdown-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 9999;
    background: transparent;
    pointer-events: auto;
}

@keyframes pulse {
    0%, 100% {
        opacity: 1;
    }
    50% {
        opacity: 0.5;
    }
}

@keyframes shimmer {
    0% {
        transform: translateX(-100%);
    }
    100% {
        transform: translateX(100%);
    }
}

@keyframes slideDown {
    from {
        opacity: 0;
        max-height: 0;
    }
    to {
        opacity: 1;
        max-height: 200px;
    }
}

@media (max-width: 768px) {
    .chatbot-custom-dropdown {
        right: 8px !important;
        left: auto !important;
        transform: none !important;
        min-width: 300px !important;
    }
}
</style>

